import { Link } from "react-router-dom";
import { useListFeaturedPaintings, useListOnSalePaintings, useListRecentPaintings, useListCategories, useListCollections, useGetGallerySummary } from "@/lib/api";
import { PaintingCard } from "@/components/painting-card";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocale } from "@/context/locale-context";

export default function Home() {
  const { t, formatPrice } = useLocale();
  const { data: featured, isLoading: isLoadingFeatured } = useListFeaturedPaintings();
  const { data: onSale } = useListOnSalePaintings();
  const { data: recent, isLoading: isLoadingRecent } = useListRecentPaintings();
  const { data: categories } = useListCategories();
  const { data: collections } = useListCollections();
  const { data: summary } = useGetGallerySummary();
  const categoryImages = [
  "/img/1.jpeg",
  "/img/2.jpeg",
  "/img/3.jpeg",
];


  return (
    <div className="flex flex-col min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-[60vh] md:h-[80vh] flex items-center justify-center overflow-hidden bg-muted" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img src="img/tigre.jpeg" alt="Atelier Gallery" className="w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/20 to-transparent mix-blend-multiply" />
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto flex flex-col items-center">
          <h1 className="text-3xl sm:text-5xl md:text-5xl font-serif text-foreground mb-4 md:mb-6 leading-tight tracking-tight animate-in fade-in slide-in-from-bottom-8 duration-1000">{t.home.heroTitle}</h1>
          <p className="text-base md:text-xl text-foreground/80 mb-8 md:mb-10 max-w-2xl font-light animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200 fill-mode-both drop-shadow-[0_4px_8px_rgba(0,0,0,0.9)]">{t.home.heroSubtitle}</p>
          <div className="animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300 fill-mode-both">
            <Link to="/gallery"><Button size="lg" className="rounded-none px-8 font-medium tracking-widest uppercase text-sm h-14" data-testid="explore-collection-btn">{t.home.heroCta}</Button></Link>
          </div>
        </div>
      </section>

      <section className="py-8 md:py-12 container mx-auto px-4 md:px-8">
  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 items-center max-w-4xl mx-auto">
    <div className="overflow-hidden bg-muted aspect-[4/3]">
      <img
        src="https://res.cloudinary.com/dpgqeudvk/image/upload/v1778122833/IMG-20190907-WA0013.jpg_1_q9j6xf.jpg"
        alt="Retrato del artista"
        className="w-full h-full object-cover"
      />
    </div>

    <div className="max-w-sm">
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
        Perfil del artista
      </p>

      <h2 className="text-2xl md:text-3xl font-serif mb-3">
        Conoce al artista
      </h2>

      <p className="text-sm text-muted-foreground mb-4 leading-6">
        Una mirada cercana a su inspiración y trayectoria.
      </p>

      <Link to="/artist">
        <Button className="rounded-none px-5 uppercase tracking-widest text-xs h-10">
          Conoce al artista
        </Button>
      </Link>
    </div>
  </div>
</section>

      {/* Gallery Stats Strip */}
      <section className="border-y bg-card" data-testid="stats-section">
        <div className="container mx-auto px-4 py-6 md:py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 md:divide-x divide-border/50 text-center">
            <div><div className="text-2xl md:text-3xl font-serif mb-1">{summary?.totalPaintings ?? '-'}</div><div className="text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground">{t.home.originalWorks}</div></div>
            <div><div className="text-2xl md:text-3xl font-serif mb-1">{summary?.soldCount ?? '-'}</div><div className="text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground">{t.home.privateCollections}</div></div>
            <div><div className="text-2xl md:text-3xl font-serif mb-1">{summary?.onSaleCount ?? '-'}</div><div className="text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground">{t.home.currentlyOnSale}</div></div>
            <div><div className="text-xl md:text-3xl font-serif mb-1">{summary?.priceRange ? `${formatPrice(summary.priceRange.min)} – ${formatPrice(summary.priceRange.max)}` : '-'}</div><div className="text-[10px] md:text-xs uppercase tracking-widest text-muted-foreground">{t.home.priceRange}</div></div>
          </div>
        </div>
      </section>

      {/* Featured Works */}
      <section className="py-12 md:py-24 container mx-auto px-4 md:px-8" data-testid="featured-section">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 md:mb-12 gap-4">
          <div><h2 className="text-3xl font-serif mb-2">{t.home.featuredWorks}</h2><p className="text-muted-foreground">{t.home.featuredSubtitle}</p></div>
          <Link to="/gallery?featured=true" className="hidden md:flex items-center gap-2 text-sm uppercase tracking-widest hover:text-accent transition-colors">{t.home.viewAllFeatured} <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {isLoadingFeatured ? Array(4).fill(0).map((_, i) => <Skeleton key={i} className="aspect-[4/5] w-full rounded-none" />) : featured?.length ? featured.slice(0, 4).map(painting => <PaintingCard key={painting.id} painting={painting} />) : <div className="col-span-full py-12 text-center text-muted-foreground">{t.home.noFeatured}</div>}
        </div>
      </section>

     {/* Categories / Collections */}
<section className="py-12 md:py-24 bg-card border-y" data-testid="collections-section">
  <div className="container mx-auto px-4 md:px-8">
    <h2 className="text-3xl font-serif mb-12 text-center">{t.home.exploreCollections}</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {categories?.slice(0, 3).map((category, index) => (
        <Link key={category.id} to={`/gallery?categoryId=${category.id}`} className="group block text-center">
          <div className="aspect-square bg-muted mb-6 overflow-hidden relative">
            <img
              src={category.imageUrl || categoryImages[index]}
              alt={category.name}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-foreground/5 group-hover:bg-transparent transition-colors z-10" />
            <div className="absolute inset-0 flex items-center justify-center p-8"><span className="font-serif text-2xl group-hover:scale-110 transition-transform duration-500">{category.name}</span></div>
          </div>
          <h3 className="font-medium text-lg uppercase tracking-wider group-hover:text-accent transition-colors">{category.name}</h3>
          <p className="text-muted-foreground text-sm mt-2">{category.description || t.home.exploreCollection}</p>
        </Link>
      ))}
    </div>
    <div className="mt-16 text-center"><Link to="/gallery"><Button variant="outline" className="rounded-none px-8 uppercase tracking-widest">{t.home.viewCompleteCatalog}</Button></Link></div>
  </div>
</section>
{/* Explore Collections (static) */}
<section className="py-12 md:py-24 bg-card border-y" data-testid="explore-collections-section">
  <div className="container mx-auto px-4 md:px-8">
    <h2 className="text-3xl font-serif mb-12 text-center">{t.home.collectionsTitle}</h2>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {collections?.slice(0, 3).map((collection, index) => (
        <Link key={collection.id} to={`/gallery?collectionId=${collection.id}`} className="group block text-center">
          <div className="aspect-square bg-muted mb-6 overflow-hidden relative">
            <img
              src={collection.imageUrl || `/img/${index + 1}.jpeg`}
              alt={collection.name}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-foreground/5 group-hover:bg-transparent transition-colors z-10" />
            <div className="absolute inset-0 flex items-center justify-center p-8">
              <span className="font-serif text-2xl text-white drop-shadow-lg group-hover:scale-110 transition-transform duration-500">{collection.name}</span>
            </div>
          </div>
          <h3 className="font-medium text-lg uppercase tracking-wider group-hover:text-accent transition-colors">{collection.name}</h3>
        </Link>
      ))}
    </div>
  </div>
</section>

      {/* New Additions */}
      <section className="py-12 md:py-24 container mx-auto px-4 md:px-8" data-testid="recent-section">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end mb-8 md:mb-12 gap-4">
          <div><h2 className="text-3xl font-serif mb-2">{t.home.recentAdditions}</h2><p className="text-muted-foreground">{t.home.recentSubtitle}</p></div>
          <Link to="/gallery" className="hidden md:flex items-center gap-2 text-sm uppercase tracking-widest hover:text-accent transition-colors">{t.home.viewCatalog} <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {isLoadingRecent ? Array(3).fill(0).map((_, i) => <Skeleton key={i} className="aspect-[4/5] w-full rounded-none" />) : recent?.length ? recent.slice(0, 3).map(painting => <PaintingCard key={painting.id} painting={painting} />) : <div className="col-span-full py-12 text-center text-muted-foreground">{t.home.noRecent}</div>}
        </div>
      </section>

      {/* On Sale Banner */}
      {onSale && onSale.length > 0 && (
        <section className="py-24 bg-accent text-accent-foreground" data-testid="on-sale-section">
          <div className="container mx-auto px-4 md:px-8 text-center max-w-3xl">
            <h2 className="text-4xl font-serif mb-6">{t.home.privateSale}</h2>
            <p className="text-lg mb-10 opacity-90 font-light">{t.home.privateSaleSubtitle}</p>
            <Link to="/gallery?onSale=true"><Button variant="outline" size="lg" className="rounded-none px-12 bg-transparent text-accent-foreground border-accent-foreground hover:bg-accent-foreground hover:text-accent uppercase tracking-widest h-14">{t.home.viewSaleCollection}</Button></Link>
          </div>
        </section>
      )}
    </div>
  );
}
